import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import api from '../services/api';
import { Button } from './ui/button';
import { IdentityMemoryCard, IdentityIssue, identityErrorDetail } from './IdentityMemory';

/**
 * PSB row identity status. "Validate & Map" stays the row decision; confirming a reusable
 * identity is a separate, eligibility-checked action qualified to this row's source site.
 */
export default function PsbIdentityPanel({ runId, mcId, canEdit }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState(null);
  const [error, setError] = useState(null);
  const tp = `psb-identity-${mcId}`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/matching/identities/psb/row', { params: { run_id: runId, mc_id: mcId } });
      setInfo(data);
      setError(null);
    } catch (err) {
      setError('Identity status unavailable');
    }
  }, [runId, mcId]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (correct) => {
    setBusy(true);
    setIssue(null);
    try {
      await api.post('/matching/identities/psb/confirm', {
        run_id: runId, mc_id: mcId, product_id: info.promotion.product.id, correct_memory: correct,
        reason: correct ? 'Corrected during PSB review' : null,
      });
      await load();
    } catch (err) {
      setIssue(identityErrorDetail(err) || { message: 'Could not remember this identity' });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="text-[10px] text-slate-400" data-testid={`${tp}-error`}>{error}</div>;
  if (!info) return null;
  const promo = info.promotion;
  return (
    <div className="border border-slate-200 rounded px-2 py-1.5 space-y-1 text-[10px]" data-testid={`${tp}-panel`}>
      <div className="font-semibold text-slate-700">Remembered identity · source site {info.house || '—'} · item {info.item_code || '—'}</div>
      {info.memory && <IdentityMemoryCard memory={info.memory} canEdit={canEdit} testIdPrefix={tp} onChanged={load} />}
      {!info.memory && info.revoked_memory && <IdentityMemoryCard memory={info.revoked_memory} canEdit={canEdit} testIdPrefix={tp} onChanged={load} />}
      {info.legacy_mapping && (
        <div className="flex items-start gap-1 text-slate-600" data-testid={`${tp}-legacy`}>
          <History className="h-3 w-3 shrink-0 mt-0.5" />
          <span>
            {info.legacy_mapping.label}: {info.legacy_mapping.product?.product_name}
            {info.legacy_mapping.approved_by ? ` · approved by ${info.legacy_mapping.approved_by}` : ''}
            {info.legacy_mapping.approved_at ? ` on ${new Date(info.legacy_mapping.approved_at).toLocaleDateString()}` : ''}
            {info.legacy_mapping.admin_overridden ? ` · admin override${info.legacy_mapping.admin_override_reason ? ` (${info.legacy_mapping.admin_override_reason})` : ''}` : ''}
          </span>
        </div>
      )}
      {promo && (
        <div className="space-y-1" data-testid={`${tp}-promotion`}>
          {promo.eligible ? (
            canEdit && (
              <Button size="sm" className="h-6 text-[10px]" disabled={busy} onClick={() => confirm(promo.replaces_memory)} data-testid={`${tp}-confirm`}>
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : `${promo.replaces_memory ? 'Correct remembered identity to' : 'Same product · remember for source site'} ${promo.replaces_memory ? promo.product?.product_name : info.house}`}
              </Button>
            )
          ) : (
            <div className="text-amber-800" data-testid={`${tp}-not-eligible`}>
              {promo.product?.product_name} cannot be remembered as the same product: {(promo.reasons || []).join('; ')}
            </div>
          )}
          <div className="text-slate-500">Remembering keeps the original mapping, override and history unchanged, and is never offered at other source sites.</div>
        </div>
      )}
      {!info.memory && !promo && (
        <div className="text-slate-500" data-testid={`${tp}-hint`}>Validate &amp; Map records this row's decision only; it does not create a reusable identity. Once the row is mapped you can confirm it here.</div>
      )}
      <IdentityIssue issue={issue} busy={busy} testIdPrefix={tp} onDismiss={() => setIssue(null)} onCorrect={() => confirm(true)} />
    </div>
  );
}
